import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { SlottingAppointment } from "./slottingAppointment";
import { SlottingWaitlist } from "./slottingWaitlist";

type OverlayType = "appointment" | "waitlist" | null;

export function SlottingOverlay() {
  const [overlayType, setOverlayType] = useState<OverlayType>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    function checkParams() {
      const params = new URLSearchParams(window.location.search);
      const appointmentId = params.get("appointmentId");
      const waitlistEntryId = params.get("waitlistEntryId");
      if (appointmentId) {
        setOverlayType("appointment");
        setToken(appointmentId);
      } else if (waitlistEntryId) {
        setOverlayType("waitlist");
        setToken(waitlistEntryId);
      } else {
        setOverlayType(null);
        setToken(null);
      }
    }

    checkParams();
    window.addEventListener("popstate", checkParams);
    return () => window.removeEventListener("popstate", checkParams);
  }, []);

  // Escape key handler
  useEffect(() => {
    if (!overlayType || !token) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") handleClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  });

  // Scroll lock
  useEffect(() => {
    if (!overlayType || !token) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [overlayType, token]);

  function handleClose() {
    const url = new URL(window.location.href);
    url.searchParams.delete("appointmentId");
    url.searchParams.delete("waitlistEntryId");
    window.history.replaceState({}, "", url.toString());
    setOverlayType(null);
    setToken(null);
  }

  const isOpen = overlayType !== null && token !== null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 100000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Backdrop */}
          <motion.div
            style={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.5)" }}
            onClick={handleClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />

          {/* Modal */}
          <motion.div
            style={{
              position: "relative",
              backgroundColor: "var(--wb-background, #fff)",
              borderRadius: 12,
              padding: 24,
              maxWidth: 560,
              width: "calc(100% - 32px)",
              minWidth: 320,
              maxHeight: "85vh",
              overflow: "auto",
            }}
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <button
              onClick={handleClose}
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                background: "none",
                border: "none",
                fontSize: 22,
                cursor: "pointer",
                color: "#64748b",
                lineHeight: 1,
                padding: 4,
              }}
            >
              &times;
            </button>

            {overlayType === "appointment" && <SlottingAppointment appointmentId={token!} />}
            {overlayType === "waitlist" && <SlottingWaitlist waitlistEntryId={token!} />}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
